import React, { useRef, useState, useCallback } from "react";
import { Toast } from "primereact/toast";
import { useTranslation } from "react-i18next";
import { Button } from "primereact/button";

import BasketTable from "./components/BasketTable";
import ConvertOrderDialog from "./components/ConvertOrderDialog";
import ImportBasketDialog from "./components/ImportBasketDialog";
import { useBasket } from "./hooks/useBasket";
import { useLanguage } from "../../contexts/LanguageContext";
import { useLazyGetBasketPdfDataQuery } from "../../api/endpoints/basket";
import BasketPdfForm from "./utils/BasketPdfForm";

const BasketPage = () => {
  const toast = useRef(null);
  const { t } = useTranslation();
  const { th, getCurrentLanguageId } = useLanguage();
  const [convertOpen, setConvertOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [getBasketPdfData, { isFetching: isDownloadingPdf }] = useLazyGetBasketPdfDataQuery();

  const {
    basket,
    isLoading,
    isError,
    refetch,
    removeItem,
    clear,
    updateQuantity,
    updateDeliveryDate, 
    convertToOrder,
    triggerPrefill,
    prefill,
    isUpdatingQuantity,
    isUpdatingDeliveryDate,
    isRemoving,
    isClearing,
    isConverting,
  } = useBasket();

  const isBusy =
    isLoading || isUpdatingQuantity || isUpdatingDeliveryDate || isRemoving || isClearing || isConverting || isDownloadingPdf;

  const handleUpdateQuantity = async (itemCode, quantity) => {
    try {
      await updateQuantity({ itemCode, quantity });
      toast.current?.show({
        severity: "success",
        summary: t("common.success"),
        detail: t("basket.updated"),
        life: 3000,
      });
      refetch();
    } catch {
      // Hata mesajı baseQueryWithReauth tarafından global olarak gösteriliyor
      // Bu yüzden burada ek toast göstermiyoruz
    }
  };

  const handleUpdateDeliveryDate = async (basketLineId, deliveryDate) => {
    try {
      await updateDeliveryDate({ basketLineId, deliveryDate: deliveryDate.toISOString() });
      toast.current?.show({
        severity: "success",
        summary: t("common.success"),
        detail: t("basket.deliveryDateUpdated"),
        life: 3000,
      });
      refetch();
    } catch {
      toast.current?.show({
        severity: "error",
        summary: t("common.error"),
        detail: t("basket.errorUpdatingDeliveryDate"),
        life: 3000,
      });
    }
  };

  const handleRemoveItem = async (itemCode) => {
    try {
      await removeItem(itemCode).unwrap();
      toast.current?.show({
        severity: "success",
        summary: t("common.success"),
        detail: t("basket.removed"),
        life: 3000,
      });
      refetch();
    } catch {
      toast.current?.show({
        severity: "error",
        summary: t("common.error"),
        detail: t("basket.errorRemoving"),
        life: 3000,
      });
    }
  };

  const handleClearBasket = async () => {
    try {
      await clear().unwrap();
      toast.current?.show({
        severity: "success",
        summary: t("common.success"),
        detail: t("basket.cleared"),
        life: 3000,
      });
      refetch();
    } catch {
      toast.current?.show({
        severity: "error",
        summary: t("common.error"),
        detail: t("basket.errorClearing"),
        life: 3000,
      });
    }
  };

  const openConvertModal = async () => {
    try {
      await triggerPrefill(getCurrentLanguageId()).unwrap();
      setConvertOpen(true);
    } catch {
      toast.current?.show({
        severity: "error",
        summary: t("common.error"),
        detail: t("basket.prefillError"),
        life: 3000,
      });
    }
  };

  const submitConvert = async (payload) => {
    try {
      await convertToOrder(payload).unwrap();
      setConvertOpen(false);
      toast.current?.show({
        severity: "success",
        summary: t("common.success"),
        detail: t("basket.orderCreated"),
        life: 3000,
      });
      refetch();
    } catch {
      // Hata mesajı baseQueryWithReauth tarafından global olarak gösteriliyor
      // Bu yüzden burada ek toast göstermiyoruz
    }
  };

  const handleDownloadPdf = useCallback(async () => {
    if (!basket?.lines?.length) {
      toast.current?.show({
        severity: "warn",
        summary: t("common.warning"),
        detail: t("basket.pdf.emptyBasket"),
        life: 3000,
      });
      return;
    }

    try {
      const data = await getBasketPdfData().unwrap();
      const pdfTranslations = {
        title: t("basket.pdf.title"),
        dealerName: t("basket.pdf.dealerName"),
        printDate: t("basket.pdf.printDate"),
        itemCode: t("basket.pdf.itemCode"),
        partName: t("basket.pdf.partName"),
        uom: t("basket.pdf.uom"),
        quantity: t("basket.pdf.quantity"),
        unitPrice: t("basket.pdf.unitPrice"),
        currency: t("basket.pdf.currency"),
        totalPrice: t("basket.pdf.totalPrice"),
        grandTotal: t("basket.pdf.grandTotal"),
      };

      const pdfForm = new BasketPdfForm();
      const pdfDoc = pdfForm.generate(data, pdfTranslations);
      pdfDoc.download("Basket_Summary.pdf");
    } catch {
      toast.current?.show({
        severity: "error",
        summary: t("common.error"),
        detail: t("basket.pdf.downloadError"),
        life: 3000,
      });
    }
  }, [basket, getBasketPdfData, t]);

  if (isLoading && !basket) return <p>{t("basket.loading")}</p>;
  if (isError) return <p>{t("basket.error")}</p>;

  return (
    <>
      <Toast ref={toast} />

      <div className="card mb-3">
        <div className="flex justify-content-between align-items-center p-3">
          <h2 className="m-0">{t("basket.title")}</h2>

          <div className="d-flex gap-2 align-items-center">
            <Button
              label={th("basket.importFromExcel")}
              icon="pi pi-file-import"
              className="p-button-secondary mr-1"
              onClick={() => setImportOpen(true)}
              disabled={isBusy}
            />

            {basket?.lines?.length > 0 && (
              <>
                <Button
                  label={t("basket.pdf.downloadPdf")}
                  icon="pi pi-file-pdf"
                  className="p-button-info mr-1"
                  onClick={handleDownloadPdf}
                  disabled={isBusy}
                  loading={isDownloadingPdf}
                />
                <Button
                  label={t("basket.clearBasket")}
                  icon="pi pi-trash"
                  className="p-button-danger mr-1"
                  onClick={handleClearBasket}
                  disabled={isBusy}
                  loading={isClearing}
                />
                <Button
                  label={t("basket.createOrder")}
                  icon="pi pi-check"
                  className="p-button-primary"
                  onClick={openConvertModal}
                  disabled={isBusy}
                  loading={isConverting}
                />
              </>
            )}
          </div>
        </div>

        <BasketTable
          basket={basket}
          onUpdateQuantity={handleUpdateQuantity}
          onUpdateDeliveryDate={handleUpdateDeliveryDate}
          onRemoveItem={handleRemoveItem}
          onClearBasket={handleClearBasket}
          onConvertToOrder={openConvertModal}
          isLoading={isBusy}
          refetchBasket={refetch}
        />
      </div>

      <ConvertOrderDialog
        visible={convertOpen}
        onHide={() => setConvertOpen(false)}
        prefill={prefill}
        basket={basket}
        busy={isConverting}
        t={t}
        onSubmit={submitConvert}
      />

      <ImportBasketDialog
        visible={importOpen}
        onHide={() => setImportOpen(false)}
        onImported={refetch}
        t={t}
      />
    </>
  );
};

export default BasketPage;
